import { useState, useEffect, useCallback } from 'react';
import { Search, Link, Unlink, ExternalLink, Loader2 } from 'lucide-react';
import { useList } from '@refinedev/core';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

interface Order {
  id: number;
  status: string;
  total: string;
  currency: string;
  createdAt: string;
  user?: {
    id: number;
    firstName?: string;
    lastName?: string;
    username?: string;
  };
}

interface LinkToOrderModalProps {
  open: boolean;
  onClose: () => void;
  chatId: number;
  chatTitle: string;
  currentOrderId?: number | null;
  onLink: (chatId: number, orderId: number) => Promise<boolean>;
  onUnlink: (chatId: number, orderId: number) => Promise<boolean>;
}

export function LinkToOrderModal({
  open,
  onClose,
  chatId,
  chatTitle,
  currentOrderId,
  onLink,
  onUnlink,
}: LinkToOrderModalProps) {
  const [search, setSearch] = useState('');
  const [isLinking, setIsLinking] = useState(false);

  // Fetch orders with search
  const ordersQuery = useList<Order>({
    resource: 'orders',
    pagination: { pageSize: 20 },
    filters: search ? [{ field: 'q', operator: 'contains', value: search }] : [],
    sorters: [{ field: 'createdAt', order: 'desc' }],
    queryOptions: {
      enabled: open,
    },
  });

  const isLoading = ordersQuery.query?.isLoading ?? false;
  const orders: Order[] = ordersQuery.result?.data ?? [];

  // Reset search when modal closes
  useEffect(() => {
    if (!open) {
      setSearch('');
    }
  }, [open]);

  const handleLink = useCallback(async (orderId: number) => {
    setIsLinking(true);
    try {
      const success = await onLink(chatId, orderId);
      if (success) {
        onClose();
      }
    } finally {
      setIsLinking(false);
    }
  }, [chatId, onLink, onClose]);

  const handleUnlink = useCallback(async () => {
    if (!currentOrderId) {return;}
    setIsLinking(true);
    try {
      const success = await onUnlink(chatId, currentOrderId);
      if (success) {
        onClose();
      }
    } finally {
      setIsLinking(false);
    }
  }, [chatId, currentOrderId, onUnlink, onClose]);

  const formatCurrency = (amount: string, currency: string) => {
    const num = parseFloat(amount);
    if (!Number.isFinite(num)) {
      return `${amount} ${currency}`;
    }
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
    }).format(num);
  };

  const formatDate = (value: string) =>
    new Date(value).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PAID':
        return 'bg-green-500/10 text-green-500 border-green-500/30';
      case 'PENDING':
        return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/30';
      case 'CANCELLED':
      case 'REFUNDED':
        return 'bg-red-500/10 text-red-500 border-red-500/30';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link className="h-5 w-5" />
            Привязать чат к заказу
          </DialogTitle>
          <DialogDescription>
            Чат: {chatTitle}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Current linked order */}
          {currentOrderId && (
            <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
              <div className="flex items-center gap-2">
                <Link className="h-4 w-4 text-primary" />
                <span className="text-sm">
                  Привязан к заказу <strong>#{currentOrderId}</strong>
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={handleUnlink}
                disabled={isLinking}
              >
                {isLinking ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Unlink className="h-4 w-4 mr-1" />
                    Отвязать
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Поиск по номеру заказа или пользователю..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Orders list */}
          <ScrollArea className="h-[300px] -mx-2 px-2">
            {isLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (orders.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
                <p className="text-sm">
                  {search ? 'Заказы не найдены' : 'Нет заказов'}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {orders.map((order) => {
                  const isCurrentlyLinked = order.id === currentOrderId;
                  const userName = [order.user?.firstName, order.user?.lastName]
                    .filter(Boolean)
                    .join(' ');

                  return (
                    <div
                      key={order.id}
                      className={cn(
                        'flex items-center justify-between p-3 rounded-lg border transition-colors',
                        isCurrentlyLinked
                          ? 'bg-primary/5 border-primary/30'
                          : 'hover:bg-accent cursor-pointer'
                      )}
                      onClick={() => !isCurrentlyLinked && !isLinking && handleLink(order.id)}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">#{order.id}</span>
                          <Badge variant="outline" className={cn('text-xs', getStatusColor(order.status))}>
                            {order.status}
                          </Badge>
                          {isCurrentlyLinked && (
                            <Badge variant="secondary" className="text-xs">
                              Привязан
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                          <span>{userName || 'Без имени'}</span>
                          {order.user?.username && (
                            <span className="text-primary/70">@{order.user.username}</span>
                          )}
                          <span>•</span>
                          <span>{formatDate(order.createdAt)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-3">
                        <span className="font-medium text-sm">
                          {formatCurrency(order.total, order.currency)}
                        </span>
                        {!isCurrentlyLinked && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(`/orders/${order.id}`, '_blank');
                            }}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
}
